"use client"

import {
  type ComponentPropsWithoutRef,
  type ComponentRef,
  type Ref,
} from "react"
import { useHasObtrusiveScrollbars } from "@/hooks/use-has-obtrusive-scrollbars"
import { ScrollArea as ScrollAreaPrimitive } from "@base-ui/react/scroll-area"
import { cn } from "cn"

interface RootProps extends ComponentPropsWithoutRef<
  typeof ScrollAreaPrimitive.Root
> {
  verticalScrollBarClassName?: string
  horizontalScrollBarClassName?: string
  viewportClassName?: string
  contentClassName?: string
  fitContent?: boolean
  /**
   * Use the browser scrollbar. Defaults to overlay scrollbars staying native
   * and classic scrollbars using the custom bar.
   */
  native?: boolean
}

const rootClass = cn("relative overflow-hidden print:overflow-visible")
const viewportClass = cn(
  "flex size-full flex-col overflow-auto overscroll-none rounded-[inherit]"
)
const contentClass = cn("min-h-full w-full shrink-0 grow rounded-[inherit]")

function ScrollArea({
  ref,
  className,
  children,
  verticalScrollBarClassName,
  horizontalScrollBarClassName,
  viewportClassName,
  contentClassName,
  onScroll,
  fitContent,
  native,
  ...props
}: RootProps & {
  ref?: Ref<ComponentRef<typeof ScrollAreaPrimitive.Viewport>>
}) {
  const hasObtrusiveScrollbars = useHasObtrusiveScrollbars()
  const useNativeScroll = native ?? !hasObtrusiveScrollbars

  if (useNativeScroll) {
    const { style, ...htmlProps } = props
    const nativeStyle = typeof style === "function" ? undefined : style

    return (
      <div
        className={cn(rootClass, className)}
        style={nativeStyle}
        {...htmlProps}
      >
        <div
          ref={ref}
          onScroll={onScroll}
          data-slot="scroll-area-viewport"
          className={cn(viewportClass, viewportClassName)}
        >
          <div
            data-slot="scroll-area-content"
            className={cn(contentClass, contentClassName)}
            style={{ minWidth: fitContent ? "fit-content" : undefined }}
          >
            {children}
          </div>
        </div>
      </div>
    )
  }

  return (
    <ScrollAreaPrimitive.Root className={cn(rootClass, className)} {...props}>
      <ScrollAreaPrimitive.Viewport
        ref={ref}
        onScroll={onScroll}
        data-slot="scroll-area-viewport"
        className={cn(viewportClass, "scrollbar-hidden", viewportClassName)}
      >
        <ScrollAreaPrimitive.Content
          data-slot="scroll-area-content"
          className={cn(
            contentClass,
            // base ui scroll area puts a `style` of `fit-content`
            // on this element which we will override by default since
            // we're typically only using scroll areas for vertical areas
            !fitContent && "min-w-0!",
            contentClassName
          )}
        >
          {children}
        </ScrollAreaPrimitive.Content>
      </ScrollAreaPrimitive.Viewport>
      <ScrollBar
        orientation="vertical"
        className={verticalScrollBarClassName}
      />
      <ScrollBar
        orientation="horizontal"
        className={horizontalScrollBarClassName}
      />
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  )
}

ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName

function ScrollBar({
  ref,
  className,
  orientation = "vertical",
  ...props
}: ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Scrollbar> & {
  ref?: Ref<ComponentRef<typeof ScrollAreaPrimitive.Scrollbar>>
}) {
  return (
    <ScrollAreaPrimitive.Scrollbar
      ref={ref}
      orientation={orientation}
      data-slot="scroll-area-scrollbar"
      className={cn(
        "group pointer-events-none relative z-1 flex animate-in touch-none rounded-full p-0.5 opacity-0 transition-opacity duration-300 select-none data-hovering:pointer-events-auto data-hovering:opacity-100 data-hovering:delay-0 data-scrolling:pointer-events-auto data-scrolling:opacity-100 data-scrolling:duration-0 print:hidden",
        orientation === "vertical" && "w-2.5",
        orientation === "horizontal" && "h-2.5 flex-col",
        className
      )}
      {...props}
    >
      <ScrollAreaPrimitive.Thumb className="flex-1 rounded-full bg-foreground/10 transition-colors group-hover:bg-foreground/20 before:absolute before:top-1/2 before:left-1/2 before:size-full before:min-h-4 before:min-w-4 before:-translate-1/2 before:content-['']" />
    </ScrollAreaPrimitive.Scrollbar>
  )
}

ScrollBar.displayName = ScrollAreaPrimitive.Scrollbar.displayName

export { ScrollArea }
